'use client';

import { useState } from 'react';
import {
  ModalBody,
  ModalFooter,
  DealerLocatorConfirmButton,
  DealerLocatorCancelButton,
  Conditional
} from '@cat-ecom/pcc-components';
import { useTranslations } from 'next-intl';
import { useUserConsentModal } from './user-consent-modal-context';
import { DealerLocatorTermsWrapper } from 'components/dealer-locator/dealer-locator-content/dealer-locator-footer/dealer-locator-terms-wrapper';
import { FormProvider, useForm } from 'react-hook-form';
import { TERMS_CHECKED } from 'components/dealer-locator/dealer-locator-content/constants';
import { useFormState } from 'react-dom';
import { selectStore } from 'services/dealer-locator/actions';
import {
  SelectStoreResponse,
  SelectedStore
} from 'services/dealer-locator/types';
import { SELECT_STORE_INITIAL_STATE } from 'services/dealer-locator/constants';
import { v4 as uuid } from 'uuid';
import { USER_CONSENT_FORM_ID } from '@cat-ecom/pcc-components/src/components/dealer-locator/constants';
import { UserConsentModalProps } from './types';
import { UserConsentError } from './user-consent-error';
import { UserConsentDescription } from './user-consent-description';
import { useUserConsentLoader } from './user-consent-loader-context';
import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { CatLoadingIndicator } from 'blocks-nextjs/blocks-react/components/LoadingIndicator';

export const UserConsentContent = ({
  dealerName,
  dealerStoreId,
  storeLocationName,
  storeLocationId
}: UserConsentModalProps) => {
  const t = useTranslations();
  const userConsentModal = useUserConsentModal();
  const userConsentFormMethods = useForm();
  const { isUserConsentLoading } = useUserConsentLoader();
  const [selectStoreState, selectStoreAction] = useFormState<
    SelectStoreResponse,
    SelectedStore
  >(selectStore, SELECT_STORE_INITIAL_STATE);

  const [isSelectStoreError, setIsSelectStoreError] = useState(false);

  const isErrorOrLoading = isUserConsentLoading || isSelectStoreError;
  const continueBtnIsDisabled =
    !userConsentFormMethods.watch(TERMS_CHECKED) || isUserConsentLoading;

  const selectStoreWithParams = selectStoreAction.bind(null, {
    id: storeLocationId,
    storeId: dealerStoreId,
    dealerName,
    isRegistration: true
  });
  const formId = `${uuid()}-${USER_CONSENT_FORM_ID}`;

  return (
    <FormProvider {...userConsentFormMethods}>
      <CatHeading variant="title" slot="header">
        {t('REGISTER_MODAL_5_USER_CONSENT_HEADER')}
      </CatHeading>
      <ModalBody>
        <Conditional test={isSelectStoreError}>
          <UserConsentError dealerStoreId={dealerStoreId} />
        </Conditional>
        <form action={selectStoreWithParams} id={formId}>
          <UserConsentDescription
            dealerName={dealerName}
            selectStoreAction={selectStoreAction}
            selectStoreState={selectStoreState}
            setIsSelectStoreError={setIsSelectStoreError}
            storeLocationName={storeLocationName}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <DealerLocatorTermsWrapper
          dealerName={dealerName}
          isDisabled={isErrorOrLoading}
          storeId={dealerStoreId}
        />
        <DealerLocatorConfirmButton
          disabled={continueBtnIsDisabled}
          form={formId}
        >
          {t('EF_CONTINUE')}
          {isUserConsentLoading && <CatLoadingIndicator small slot="after" />}
        </DealerLocatorConfirmButton>
        <DealerLocatorCancelButton
          onCancel={userConsentModal.modalControl.close}
        >
          {t('CANCEL')}
        </DealerLocatorCancelButton>
      </ModalFooter>
    </FormProvider>
  );
};
